import { useEffect, useSyncExternalStore } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { isNativeApp } from "../../../lib/platform/native";
import { watchMetaOverlay } from "../../../lib/firebase/metaOverlay";
import { isPurchaseAvailable } from "../purchaseAvailability";
import { readNativeTrial, resolveTrialOffer, subscribeNativeTrial } from "../trialOffer";

/**
 * Dias de teste grátis que ESTA pessoa pode ver anunciados numa porta do PRO,
 * ou `null` para a porta não prometer teste. Toda a regra mora em
 * `trialOffer.ts#resolveTrialOffer` — este hook só junta as entradas.
 *
 * Barato de propósito: o perfil já está no `AuthContext`; a loja NÃO é
 * consultada aqui (ver o cabeçalho de `trialOffer.ts`); e o override de dias
 * do painel `/admin` (`pro_showcase/config.trialDays`, o mesmo que a `/pro` lê
 * por `useProShowcaseOverlay`) vem de UM listener de módulo, aberto na
 * primeira porta montada com conta real — são ~10 portas, e
 * `useProShowcaseOverlay` abre um listener por instância.
 */

let overrideDays: number | null = null;
let overrideStarted = false;
const overrideListeners = new Set<() => void>();

function startOverrideListener(): void {
  if (overrideStarted) return;
  overrideStarted = true;
  watchMetaOverlay<{ trialDays?: unknown }>("pro_showcase", (byId) => {
    // A MESMA leitura de `useProShowcaseOverlay` (`doc?.trialDays ?? null`): um
    // filtro próprio aqui (ex.: descartar 0) faria o painel em 0 calar a `/pro`
    // e as portas voltarem a prometer os 7 dias do código.
    const raw = byId.get("config")?.trialDays;
    const next = typeof raw === "number" ? raw : null;
    if (next === overrideDays) return;
    overrideDays = next;
    overrideListeners.forEach((l) => l());
  });
}
function subscribeOverride(fn: () => void): () => void {
  overrideListeners.add(fn);
  return () => overrideListeners.delete(fn);
}
const readOverride = () => overrideDays;

/**
 * A sessão SEM exigir `AuthProvider`: as portas vivem em componentes que já
 * existiam sem depender dele (e são montados assim em harness e testes). Fora
 * do provider, `useAuth` lança DEPOIS do `useContext` — a ordem de hooks é a
 * mesma nos dois caminhos, então capturar aqui é seguro. Sem sessão = sem
 * promessa, nunca uma tela quebrada por causa de um convite.
 */
function useSessionForTrial() {
  try {
    return useAuth();
  } catch {
    return null;
  }
}

export function useTrialOffer(): number | null {
  const auth = useSessionForTrial();
  const profile = auth?.profile ?? null;
  const signedIn = !!profile && auth?.isAnonymous !== true;
  useEffect(() => {
    // Conta real primeiro: a rule de `pro_showcase` exige sessão, e um listener
    // aberto no boot frio só renderia `permission-denied`.
    if (signedIn) startOverrideListener();
  }, [signedIn]);
  const trialDaysOverride = useSyncExternalStore(subscribeOverride, readOverride, readOverride);
  const nativeTrialDays = useSyncExternalStore(subscribeNativeTrial, readNativeTrial, readNativeTrial);
  return resolveTrialOffer({
    purchaseAvailable: isPurchaseAvailable(),
    native: isNativeApp(),
    signedIn,
    isPro: auth?.effectiveIsPro === true,
    trialConsumed: typeof profile?.proTrialConsumedAt === "number",
    trialDaysOverride,
    nativeTrialDays,
  });
}
